<template>
  <form class="checkout-login__login-form" @submit.prevent="form.handleSubmit">
    <div class="checkout-login__form-grid">
      <TanStackFormInput
        :form="form"
        name="email"
        :label="t('checkout.personalDetails.form.email')"
        type="email"
        :disabled="isLoading"
        data-test-id="email"
        autocomplete="email"
        :required="true"
      />
      <TanStackFormInput
        ref="passwordInputRef"
        :form="form"
        name="password"
        :label="t('checkout.personalDetails.form.password')"
        type="password"
        :disabled="isLoading"
        data-test-id="password"
        autocomplete="password"
        :required="true"
      />
    </div>

    <button
      type="button"
      class="checkout-login__forgot-password"
      @click="handleForgotPassword"
    >
      {{ t('checkout.forgotPassword.title') }}
    </button>
    <div class="checkout-login__actions">
      <form.Subscribe>
        <template #default="{ isSubmitting }">
          <CheckoutSubmitButton
            :is-loading="isSubmitting"
            :is-disabled="isLoading"
          >
            {{ t('checkout.steps.continue') }}
          </CheckoutSubmitButton>
        </template>
      </form.Subscribe>
    </div>
  </form>
</template>

<script setup lang="ts">
import { useForm } from '@tanstack/vue-form'
import { useI18n } from 'vue-i18n'
import { ref, onMounted } from 'vue'
import { createLoginSchema, type LoginFormData } from '../../../schemas/checkoutLogin'
import TanStackFormInput from '~/components/common/tan-stack-form/TanStackFormInput.vue'
import CheckoutSubmitButton from '~/components/checkout/CheckoutSubmitButton.vue'

const { t } = useI18n()

const props = defineProps<{
  isLoading: boolean
  email: string
}>()

const emit = defineEmits<{
  (e: 'submit', data: LoginFormData): void
  (e: 'forgotPassword'): void
  (e: 'email-change', email: string): void
}>()

const form = useForm({
  defaultValues: {
    email: props.email,
    password: '',
  },
  onSubmit: ({ value }) => {
    emit('submit', value as LoginFormData)
  },
  validators: {
    onChange: createLoginSchema(t),
  },
})

const handleForgotPassword = () => {
  emit('forgotPassword')
}

const emailValue = form.useStore((state) => state.values.email)

watch(emailValue, (newEmail) => {
  emit('email-change', newEmail)
})

const passwordInputRef = ref<InstanceType<typeof TanStackFormInput> | null>(
  null
)

onMounted(() => {
  // focus on password input
  const passwordInput = passwordInputRef.value
  if (passwordInput) {
    passwordInput.focusInput()
  }
})
</script>

<style lang="postcss" scoped>
.checkout-login {
  &__form-grid {
    @apply grid grid-cols-1 md:grid-cols-2 gap-4;
  }

  &__actions {
    @apply space-y-2 mt-4;
  }

  &__submit {
    @apply mt-4 w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-active focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50;
  }

  &__forgot-password {
    @apply text-sm text-primary hover:text-primary-active mt-2 underline hover:no-underline;
  }
}
</style>
